import { useState, useEffect } from "react";

interface DateTimeRangePickerProps {
  visible: boolean;
  onClose: () => void;
  onConfirm: (range: {
    startDate: string;
    startTime: string;
    endDate: string;
    endTime: string;
  }) => void;
  initialRange?: {
    startDate: string;
    startTime: string;
    endDate: string;
    endTime: string;
  };
  title?: string;
  /** 开始时间标签（默认 "取车"） */
  startLabel?: string;
  /** 结束时间标签（默认 "还车"） */
  endLabel?: string;
  /** 错误提示模板，支持 {start} {end} 占位符 */
  errorMsgTemplate?: string;
}

// 生成日期列表
const generateDates = (days: number = 30) => {
  const dates = [];
  const today = new Date();
  for (let i = 0; i < days; i++) {
    const date = new Date(today);
    date.setDate(today.getDate() + i);
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    const weekDay = ["周日", "周一", "周二", "周三", "周四", "周五", "周六"][date.getDay()];
    dates.push({
      fullDate: `${month}-${day}`,
      month,
      day,
      weekDay,
      timestamp: date.getTime(),
    });
  }
  return dates;
};

// 生成时间列表（每小时一个）
const generateHours = () => {
  const hours = [];
  for (let i = 0; i < 24; i++) {
    const hour = String(i).padStart(2, "0");
    hours.push(`${hour}:00`);
  }
  return hours;
};

// 获取当前时间（整点，向上取整）
const getCurrentHour = () => {
  const now = new Date();
  const hour = String(now.getHours()).padStart(2, "0");
  return `${hour}:00`;
};

// 获取今天的日期字符串 (MM-DD)
const getTodayString = () => {
  const today = new Date();
  const month = String(today.getMonth() + 1).padStart(2, "0");
  const day = String(today.getDate()).padStart(2, "0");
  return `${month}-${day}`;
};

// 获取明天的日期字符串 (MM-DD)
const getTomorrowString = () => {
  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);
  const month = String(tomorrow.getMonth() + 1).padStart(2, "0");
  const day = String(tomorrow.getDate()).padStart(2, "0");
  return `${month}-${day}`;
};

// 比较两个日期时间
const compareDateTime = (
  date1: string,
  time1: string,
  date2: string,
  time2: string,
  dates: ReturnType<typeof generateDates>
) => {
  const date1Index = dates.findIndex((d) => d.fullDate === date1);
  const date2Index = dates.findIndex((d) => d.fullDate === date2);
  
  if (date1Index !== date2Index) {
    return date1Index - date2Index;
  }
  
  // 同一天，比较时间
  return time1.localeCompare(time2);
};

export default function DateTimeRangePicker({
  visible,
  onClose,
  onConfirm,
  initialRange,
  title = "选择时间",
  startLabel = "取车",
  endLabel = "还车",
  errorMsgTemplate = "{start}时间不能晚于{end}时间",
}: DateTimeRangePickerProps) {
  // 默认使用当前时间和明天
  const currentHour = getCurrentHour();
  const todayStr = getTodayString();
  const tomorrowStr = getTomorrowString();

  const defaultRange = {
    startDate: todayStr,
    startTime: currentHour,
    endDate: tomorrowStr,
    endTime: currentHour,
  };

  const [activeTab, setActiveTab] = useState<"start" | "end">("start");
  const [selection, setSelection] = useState(defaultRange);
  const [errorMsg, setErrorMsg] = useState("");

  const dates = generateDates(30);
  const hours = generateHours();

  // 初始化默认值
  useEffect(() => {
    if (visible) {
      setSelection(initialRange || defaultRange);
      setActiveTab("start");
      setErrorMsg("");
    }
    // 仅在弹窗打开时初始化一次；initialRange 通常是父组件内联对象，
    // 若作为依赖，父组件重渲染时会重置用户已选择的日期。
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible]);

  const handleDateSelect = (fullDate: string) => {
    setErrorMsg("");
    if (activeTab === "start") {
      const startIdx = dates.findIndex((d) => d.fullDate === fullDate);
      const endIdx = dates.findIndex((d) => d.fullDate === selection.endDate);
      // 入住日期晚于或等于离店日期时，自动将离店日期顺延到入住日期后一天，
      // 保证“入住-离店”始终有效，避免确认时被校验拦截。
      const nextEndDate =
        endIdx <= startIdx && dates[startIdx + 1]
          ? dates[startIdx + 1].fullDate
          : selection.endDate;
      setSelection((prev) => ({
        ...prev,
        startDate: fullDate,
        endDate: nextEndDate,
      }));
    } else {
      const startIdx = dates.findIndex((d) => d.fullDate === selection.startDate);
      const endIdx = dates.findIndex((d) => d.fullDate === fullDate);
      // 离店日期不能早于或等于入住日期
      if (endIdx <= startIdx) {
        setErrorMsg(
          errorMsgTemplate
            .replace("{start}", startLabel)
            .replace("{end}", endLabel),
        );
        return;
      }
      setSelection((prev) => ({ ...prev, endDate: fullDate }));
    }
  };

  const handleTimeSelect = (time: string) => {
    setErrorMsg("");
    if (activeTab === "start") {
      setSelection((prev) => ({ ...prev, startTime: time }));
    } else {
      setSelection((prev) => ({ ...prev, endTime: time }));
    }
  };

  const handleConfirm = () => {
    // 验证开始时间不能晚于结束时间
    const compareResult = compareDateTime(
      selection.startDate,
      selection.startTime,
      selection.endDate,
      selection.endTime,
      dates
    );

    if (compareResult > 0) {
      setErrorMsg(errorMsgTemplate.replace("{start}", startLabel).replace("{end}", endLabel));
      return;
    }

    onConfirm(selection);
    onClose();
  };

  const handleTabChange = (tab: "start" | "end") => {
    setActiveTab(tab);
    setErrorMsg("");
  };

  if (!visible) return null;

  return (
    <div
      className="fixed inset-0 bg-black/50 z-50 flex items-end"
      onClick={onClose}
    >
      <div
        className="bg-white w-full rounded-t-2xl p-4 max-h-[80vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* 标题 */}
        <div className="flex items-center justify-between mb-4">
          <span className="text-lg font-semibold">{title}</span>
          <button onClick={onClose} className="text-gray-400">
            <i className="ri-close-line" style={{ fontSize: 24 }} />
          </button>
        </div>

        {/* Tab 切换 */}
        <div className="flex bg-gray-100 rounded-lg p-1 mb-4">
          <button
            onClick={() => handleTabChange("start")}
            className={`flex-1 py-2 text-sm font-medium rounded-md transition-colors ${
              activeTab === "start"
                ? "bg-white text-amber-600 shadow-sm"
                : "text-gray-500"
            }`}
          >
            <div className="text-xs opacity-70">{startLabel}</div>
            <div>
              {selection.startDate} {selection.startTime}
            </div>
          </button>
          <button
            onClick={() => handleTabChange("end")}
            className={`flex-1 py-2 text-sm font-medium rounded-md transition-colors ${
              activeTab === "end"
                ? "bg-white text-amber-600 shadow-sm"
                : "text-gray-500"
            }`}
          >
            <div className="text-xs opacity-70">{endLabel}</div>
            <div>
              {selection.endDate} {selection.endTime}
            </div>
          </button>
        </div>

        {/* 日期选择 - 横向滚动 */}
        <div className="mb-4">
          <div className="text-sm text-gray-500 mb-2">选择日期</div>
          <div className="flex gap-2 overflow-x-auto pb-2 scrollbar-hide">
            {dates.map((date) => {
              const isSelected =
                activeTab === "start"
                  ? selection.startDate === date.fullDate
                  : selection.endDate === date.fullDate;
              return (
                <button
                  key={date.fullDate}
                  onClick={() => handleDateSelect(date.fullDate)}
                  className={`flex-shrink-0 w-14 h-16 rounded-xl flex flex-col items-center justify-center transition-colors ${
                    isSelected
                      ? "bg-amber-500 text-white"
                      : "bg-gray-50 text-gray-700"
                  }`}
                >
                  <span className="text-xs">{date.weekDay}</span>
                  <span className="text-lg font-bold">{date.day}</span>
                </button>
              );
            })}
          </div>
        </div>

        {/* 时间选择 - 网格 */}
        <div className="mb-4">
          <div className="text-sm text-gray-500 mb-2">选择时间</div>
          <div className="grid grid-cols-4 gap-2 max-h-48 overflow-y-auto">
            {hours.map((time) => {
              const isSelected =
                activeTab === "start"
                  ? selection.startTime === time
                  : selection.endTime === time;
              return (
                <button
                  key={time}
                  onClick={() => handleTimeSelect(time)}
                  className={`py-2 px-3 rounded-lg text-sm transition-colors ${
                    isSelected
                      ? "bg-amber-500 text-white"
                      : "bg-gray-50 text-gray-700"
                  }`}
                >
                  {time}
                </button>
              );
            })}
          </div>
        </div>

        {/* 错误提示 */}
        {errorMsg && (
          <div className="mb-3 text-sm text-red-500 text-center">{errorMsg}</div>
        )}

        {/* 确定按钮 */}
        <button
          onClick={handleConfirm}
          className="w-full py-3 bg-amber-500 text-white rounded-xl font-medium"
        >
          确定
        </button>
      </div>
    </div>
  );
}
